import React from "react";

import { useTranslation } from "utils";

import { Flex, KlimRadioGroup, Tooltip } from "components";

import { ViewMode } from "pages/app/scenario-analysis/utils/forecast";

/** A long view label truncates inside its pill, with the full text on hover. */
const toggleLabel = (text: string) => (
  <Tooltip title={text} placement="top" className="outcome-comparison-view-toggle-label">
    {text}
  </Tooltip>
);

interface ComparisonViewToggleProps {
  value: ViewMode;
  /** Nothing to rebase on without a comparison scenario, so the Difference pill only exists once there is one. */
  hasDifferenceView: boolean;
  onChange: (view: ViewMode) => void;
  dataTestId: string;
}

/** Pills switching the results between the margin, difference, yield and resilience views. */
export const ComparisonViewToggle = ({ value, hasDifferenceView, onChange, dataTestId }: ComparisonViewToggleProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  const options: Array<{ value: ViewMode; label: React.ReactNode }> = [
    { value: "cumulative", label: toggleLabel(t("outcomeComparison.viewCumulativeMargin")) },
    { value: "annual", label: toggleLabel(t("outcomeComparison.viewAnnualMargin")) },
    ...(hasDifferenceView ? [{ value: "difference" as const, label: toggleLabel(t("outcomeComparison.viewDifference")) }] : []),
    { value: "yield", label: toggleLabel(t("outcomeComparison.viewYield")) },
    { value: "resilience", label: toggleLabel(t("outcomeComparison.viewResilienceScore")) },
  ];

  return (
    <Flex className="outcome-comparison-view-toggle" justify="flex-end">
      <KlimRadioGroup
        value={value}
        onChange={(e) => {
          const selected = options.find((option) => option.value === e.target.value);
          if (selected) onChange(selected.value);
        }}
        options={options}
        optionType="button"
        buttonStyle="solid"
        type="tertiary"
        size="middle"
        dataTestId={dataTestId}
      />
    </Flex>
  );
};
